/**
 * Visualizzatore Conversazioni WhatsApp con AI Insights
 *
 * @example
 * ```tsx
 * import { WhatsAppProvider, ConversationsViewer } from './whatsapp-dashboard';
 *
 * function App() {
 *   return (
 *     <WhatsAppProvider config={{ apiBaseUrl: '/api' }}>
 *       <ConversationsViewer />
 *     </WhatsAppProvider>
 *   );
 * }
 * ```
 */

import React from 'react';
import { appToast } from '../../../lib/appDialog';
import { useConversations } from '../hooks';
import { useWhatsAppConfig } from '../WhatsAppProvider';

export function ConversationsViewer() {
  const { apiBaseUrl, mediaBaseUrl } = useWhatsAppConfig();

  const {
    conversations,
    selectedConversation,
    messages,
    loading,
    error,
    selectConversation,
    refreshMessages,
    deleteMessages,
    deleteConversation,
    clearLogs
  } = useConversations();

  const [showClearConfirm, setShowClearConfirm] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = React.useState<Set<number>>(new Set());
  const [selectionMode, setSelectionMode] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [conversationToDelete, setConversationToDelete] = React.useState<{id: number, name: string, count: number} | null>(null);
  const [deletingConversation, setDeletingConversation] = React.useState(false);
  const [expandedSection, setExpandedSection] = React.useState<string | null>(null); // Per accordion sezioni
  const [searchQuery, setSearchQuery] = React.useState(''); // Ricerca nei messaggi

  // Filtra messaggi in base alla ricerca
  const filteredMessages = React.useMemo(() => {
    if (!searchQuery.trim()) return messages;
    const query = searchQuery.toLowerCase();
    return messages.filter(msg =>
      (msg.content_text && msg.content_text.toLowerCase().includes(query)) ||
      (msg.transcribed_text && msg.transcribed_text.toLowerCase().includes(query)) ||
      (msg.text_version && msg.text_version.toLowerCase().includes(query)) ||
      (msg.voice_version && msg.voice_version.toLowerCase().includes(query)) ||
      (msg.ai_intent && msg.ai_intent.toLowerCase().includes(query))
    );
  }, [messages, searchQuery]);

  // Base URL per i media (derivata da apiBaseUrl se non specificata)
  const getMediaUrl = (mediaPath: string) => {
    if (mediaBaseUrl) {
      return `${mediaBaseUrl}${mediaPath}`;
    }
    // Deriva base URL rimuovendo /api dall'apiBaseUrl
    const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    return baseUrl ? `${baseUrl}${mediaPath}` : mediaPath;
  };

  // Trova coppia domanda-risposta per un messaggio
  const getMessagePairIds = (msgId: number): number[] => {
    const msgIndex = messages.findIndex(m => m.id === msgId);
    if (msgIndex === -1) return [msgId];

    const msg = messages[msgIndex];
    const pairIds: number[] = [msgId];

    if (msg.direction === 'incoming') {
      // Se è incoming, aggiungi tutti i successivi outgoing fino al prossimo incoming
      for (let i = msgIndex + 1; i < messages.length; i++) {
        if (messages[i].direction === 'outgoing') {
          pairIds.push(messages[i].id);
        } else {
          break; // Stop al prossimo incoming
        }
      }
    } else {
      // Se è outgoing, trova l'incoming precedente e tutti gli outgoing di quella coppia
      let incomingIndex = -1;
      for (let i = msgIndex - 1; i >= 0; i--) {
        if (messages[i].direction === 'incoming') {
          incomingIndex = i;
          break;
        }
      }

      if (incomingIndex !== -1) {
        pairIds.push(messages[incomingIndex].id);
        // Aggiungi tutti gli outgoing tra incoming e il prossimo incoming
        for (let i = incomingIndex + 1; i < messages.length; i++) {
          if (messages[i].direction === 'outgoing') {
            if (!pairIds.includes(messages[i].id)) {
              pairIds.push(messages[i].id);
            }
          } else {
            break;
          }
        }
      }
    }

    return pairIds;
  };

  // Toggle selezione messaggio (con coppia)
  const toggleMessageSelection = (msgId: number) => {
    const pairIds = getMessagePairIds(msgId);

    setSelectedMessageIds(prev => {
      const newSet = new Set(prev);
      // Controlla se la coppia è già selezionata
      const allSelected = pairIds.every(id => newSet.has(id));

      if (allSelected) {
        // Deseleziona tutta la coppia
        pairIds.forEach(id => newSet.delete(id));
      } else {
        // Seleziona tutta la coppia
        pairIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  // Seleziona tutti i messaggi
  const selectAllMessages = () => {
    setSelectedMessageIds(new Set(messages.map(m => m.id)));
  };

  // Deseleziona tutti
  const deselectAllMessages = () => {
    setSelectedMessageIds(new Set());
  };

  // Elimina messaggi selezionati
  const handleDeleteSelected = async () => {
    if (selectedMessageIds.size === 0) return;

    setDeleting(true);
    try {
      await deleteMessages(Array.from(selectedMessageIds));
      setSelectedMessageIds(new Set());
      setSelectionMode(false);
    } catch (err) {
      appToast.error('Errore durante l\'eliminazione dei messaggi');
    } finally {
      setDeleting(false);
    }
  };

  // Exit selection mode
  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedMessageIds(new Set());
  };


  const getSentimentColor = (sentiment: string | null) => {
    switch (sentiment) {
      case 'positive': return 'text-green-400 bg-green-900/30';
      case 'neutral': return 'text-blue-400 bg-blue-900/30';
      case 'negative': return 'text-yellow-400 bg-yellow-900/30';
      case 'angry': return 'text-red-400 bg-red-900/30';
      default: return 'text-gray-400 bg-gray-800/30';
    }
  };

  const getSentimentEmoji = (sentiment: string | null) => {
    switch (sentiment) {
      case 'positive': return '😊';
      case 'neutral': return '😐';
      case 'negative': return '😟';
      case 'angry': return '😠';
      default: return '';
    }
  };

  const handleClearLogs = async () => {
    setClearing(true);
    try {
      await clearLogs();
      setShowClearConfirm(false);
      appToast.success('Log eliminati con successo!');
    } catch (err) {
      appToast.error('Errore durante l\'eliminazione dei log');
    } finally {
      setClearing(false);
    }
  };

  // Elimina conversazione con conferma
  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;
    setDeletingConversation(true);
    try {
      await deleteConversation(conversationToDelete.id);
      setConversationToDelete(null);
    } catch (err) {
      appToast.error('Errore durante l\'eliminazione della conversazione');
    } finally {
      setDeletingConversation(false);
    }
  };

  // Formatta timestamp completo con data e ora
  const formatFullTimestamp = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-slate-700">
        <div>
          <h2 className="text-lg font-semibold text-white">Log Conversazioni WhatsApp</h2>
          <p className="text-xs text-slate-400">Cronologia messaggi con analisi AI</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshMessages}
            disabled={!selectedConversation}
            className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded text-sm font-medium transition"
          >
            🔄 Aggiorna
          </button>
          <button
            onClick={() => setShowClearConfirm(true)}
            disabled={conversations.length === 0}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded text-sm font-medium transition"
          >
            🗑️ Pulisci Log
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-900/50 border-b border-red-700 text-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="flex h-[600px]">
        {/* Lista Conversazioni */}
        <div className="w-1/3 border-r border-slate-700 overflow-y-auto">
          {loading && conversations.length === 0 && (
            <div className="p-4 text-center text-slate-400 text-sm">
              Caricamento conversazioni...
            </div>
          )}

          {!loading && conversations.length === 0 && (
            <div className="p-4 text-center text-slate-400 text-sm">
              Nessuna conversazione disponibile.<br/>
              Invia un messaggio WhatsApp al bot per iniziare.
            </div>
          )}

          <div className="divide-y divide-slate-700">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`group relative w-full text-left p-3 hover:bg-slate-700/50 transition cursor-pointer ${
                  selectedConversation?.id === conv.id ? 'bg-slate-700/70' : ''
                }`}
                onClick={() => selectConversation(conv)}
              >
                {/* Pulsante elimina - visibile on hover */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConversationToDelete({
                      id: conv.id,
                      name: conv.contact_name || conv.contact_phone,
                      count: conv.message_count
                    });
                  }}
                  className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 p-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs transition-opacity"
                  title="Elimina conversazione"
                >
                  🗑️
                </button>

                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-medium text-white text-sm truncate">
                      {conv.contact_name || 'Utente sconosciuto'}
                    </span>
                    {/* Badge messaggi non letti */}
                    {conv.unread_count && conv.unread_count > 0 && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
                        {conv.unread_count > 99 ? '99+' : conv.unread_count}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-400 flex-shrink-0 ml-2 pr-6">
                    {formatFullTimestamp(conv.last_message_at)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-400 truncate">
                    {/* Mostra numero sotto il nome se diverso */}
                    {conv.contact_name && (
                      <span className="mr-2">📱 {conv.contact_phone}</span>
                    )}
                    {conv.device_name && (
                      <span className="text-primary-400">🔧 {conv.device_name}</span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    {conv.message_count} msg
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Messaggi e Dettagli AI */}
        <div className="flex-1 flex flex-col">
          {!selectedConversation && (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <div className="text-4xl mb-2">💬</div>
                <p className="text-sm">Seleziona una conversazione per vedere i messaggi</p>
              </div>
            </div>
          )}

          {selectedConversation && (
            <>
              {/* Header Conversazione */}
              <div className="p-3 border-b border-slate-700 bg-slate-700/30">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-white">
                      {selectedConversation.contact_phone}
                    </div>
                    {selectedConversation.contact_name && (
                      <div className="text-xs text-slate-400">
                        {selectedConversation.contact_name}
                      </div>
                    )}
                  </div>

                  {/* Controlli selezione messaggi */}
                  <div className="flex items-center gap-2">
                    {!selectionMode ? (
                      <button
                        onClick={() => setSelectionMode(true)}
                        className="px-2 py-1 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded transition"
                      >
                        ☑️ Seleziona
                      </button>
                    ) : (
                      <>
                        <span className="text-xs text-slate-400">
                          {selectedMessageIds.size} msg ({Math.ceil(selectedMessageIds.size / 2)} coppie)
                        </span>
                        <button
                          onClick={selectAllMessages}
                          className="px-2 py-1 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded transition"
                        >
                          Tutti
                        </button>
                        <button
                          onClick={deselectAllMessages}
                          className="px-2 py-1 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded transition"
                        >
                          Nessuno
                        </button>
                        <button
                          onClick={handleDeleteSelected}
                          disabled={selectedMessageIds.size === 0 || deleting}
                          className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded transition"
                        >
                          {deleting ? '...' : `🗑️ Elimina (${selectedMessageIds.size})`}
                        </button>
                        <button
                          onClick={exitSelectionMode}
                          className="px-2 py-1 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded transition"
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Barra di ricerca */}
                <div className="mt-2 relative">
                  <input
                    type="text"
                    placeholder="🔍 Cerca nei messaggi..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500 placeholder-slate-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Messaggi */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {loading && messages.length === 0 && (
                  <div className="text-center text-slate-400 text-sm py-8">
                    Caricamento messaggi...
                  </div>
                )}

                {/* Risultati ricerca */}
                {searchQuery && (
                  <div className="text-xs text-slate-400 mb-2">
                    {filteredMessages.length === 0 ? (
                      <span className="text-yellow-400">⚠️ Nessun messaggio trovato per "{searchQuery}"</span>
                    ) : (
                      <span>🔍 {filteredMessages.length} messaggi trovati</span>
                    )}
                  </div>
                )}

                {filteredMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex items-start gap-2 ${msg.direction === 'incoming' ? 'justify-start' : 'justify-end'}`}
                  >
                    {/* Checkbox selezione (solo in selection mode) */}
                    {selectionMode && msg.direction === 'incoming' && (
                      <label className="flex items-center cursor-pointer mt-3">
                        <input
                          type="checkbox"
                          checked={selectedMessageIds.has(msg.id)}
                          onChange={() => toggleMessageSelection(msg.id)}
                          className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-primary-600 focus:ring-primary-500 cursor-pointer"
                        />
                      </label>
                    )}

                    <div className={`max-w-[85%] rounded-lg p-3 ${
                      msg.direction === 'incoming'
                        ? `bg-slate-700 border ${selectedMessageIds.has(msg.id) ? 'border-red-500' : 'border-slate-600'}`
                        : 'bg-primary-600/20 border border-primary-600/50'
                    } ${selectionMode && msg.direction === 'incoming' ? 'cursor-pointer' : ''}`}
                    onClick={() => selectionMode && msg.direction === 'incoming' && toggleMessageSelection(msg.id)}>
                      {/* Timestamp in alto */}
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-medium ${msg.direction === 'incoming' ? 'text-slate-400' : 'text-primary-300'}`}>
                          {msg.direction === 'incoming' ? '📥' : '📤'}
                        </span>
                        <span className="text-xs text-slate-400 bg-slate-800/50 px-2 py-0.5 rounded">
                          🕐 {msg.timestamp && !isNaN(new Date(msg.timestamp).getTime())
                            ? new Date(msg.timestamp).toLocaleString('it-IT', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            : 'Data non disponibile'}
                        </span>
                      </div>
                      {/* Messaggio */}
                      <div className="mb-2">
                        {/* Audio Player */}
                        {msg.message_type === 'audio' && msg.media_url && (
                          <div className="mb-2">
                            <div className="flex items-center gap-2 mb-1 text-xs text-slate-400">
                              <span>🎤 Audio</span>
                              {msg.duration_seconds && (
                                <span>({msg.duration_seconds}s)</span>
                              )}
                              {msg.transcribed_text && (
                                <span className="text-green-400">✓ Trascritto</span>
                              )}
                            </div>
                            <audio controls className="w-full h-8 rounded">
                              <source src={getMediaUrl(msg.media_url)} type="audio/ogg" />
                              Il tuo browser non supporta l'audio.
                            </audio>
                          </div>
                        )}

                        {/* Testo trascritto (incoming audio) */}
                        {msg.direction === 'incoming' && msg.transcribed_text && (
                          <div className="mb-1">
                            <div className="text-xs text-slate-500 mb-0.5">Trascrizione:</div>
                            <div className="text-white text-sm whitespace-pre-wrap bg-slate-800/50 p-2 rounded">
                              {msg.transcribed_text}
                            </div>
                          </div>
                        )}

                        {/* Testo audio inviato (outgoing audio) */}
                        {msg.direction === 'outgoing' && msg.message_type === 'audio' && msg.content_text && (
                          <div className="mb-1">
                            <div className="text-xs text-slate-500 mb-0.5">Testo audio inviato:</div>
                            <div className="text-white text-sm whitespace-pre-wrap bg-slate-800/50 p-2 rounded">
                              {msg.content_text}
                            </div>
                          </div>
                        )}

                        {/* Testo normale (non audio) */}
                        {msg.content_text && msg.message_type !== 'audio' && (
                          <div className="text-white text-sm whitespace-pre-wrap">
                            {msg.content_text}
                          </div>
                        )}

                        {!msg.content_text && !msg.transcribed_text && msg.message_type !== 'audio' && (
                          <div className="text-slate-400 text-sm italic">
                            [Media]
                          </div>
                        )}
                      </div>

                      {/* AI Insights (solo per messaggi in arrivo) */}
                      {msg.direction === 'incoming' && msg.ai_intent && (
                        <div className="border-t border-slate-600 pt-2 mt-2 space-y-1.5">
                          {/* Intent */}
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-slate-400">Intent:</span>
                            <span className="font-mono text-primary-400">
                              {msg.ai_intent === 'unknown' ? 'sconosciuto' : msg.ai_intent}
                            </span>
                            {msg.ai_confidence && (
                              <span className="text-slate-500">
                                ({(msg.ai_confidence * 100).toFixed(0)}%)
                              </span>
                            )}
                          </div>

                          {/* Sentiment */}
                          {msg.ai_sentiment && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-slate-400">Sentiment:</span>
                              <span className={`px-2 py-0.5 rounded ${getSentimentColor(msg.ai_sentiment)}`}>
                                {getSentimentEmoji(msg.ai_sentiment)} {msg.ai_sentiment}
                              </span>
                            </div>
                          )}

                          {/* Entities */}
                          {msg.ai_entities && (
                            (() => {
                              // Parse entities se è una stringa JSON
                              let entities = msg.ai_entities;
                              if (typeof entities === 'string') {
                                try {
                                  entities = JSON.parse(entities);
                                } catch {
                                  return null;
                                }
                              }
                              if (!entities || typeof entities !== 'object' || Object.keys(entities).length === 0) {
                                return null;
                              }
                              const formatted = Object.entries(entities)
                                .filter(([_, value]) => value !== null && value !== undefined && value !== '')
                                .map(([key, value]) => `${key}: ${value}`)
                                .join(', ');
                              if (!formatted) return null;
                              return (
                                <div className="text-xs">
                                  <span className="text-slate-400">Entità: </span>
                                  <span className="text-slate-300">{formatted}</span>
                                </div>
                              );
                            })()
                          )}

                          {/* Database Query */}
                          {!!msg.needs_database ? (
                            (() => {
                              // Parse database_query_result se è una stringa JSON
                              let dbResult = msg.database_query_result;
                              if (typeof dbResult === 'string') {
                                try {
                                  dbResult = JSON.parse(dbResult);
                                } catch {
                                  dbResult = null;
                                }
                              }

                              return (
                                <div className="text-xs">
                                  <span className="text-slate-400">Query DB: </span>
                                  {dbResult ? (
                                    (dbResult.count > 0 || (dbResult.data && dbResult.data.length > 0)) ? (
                                      <span className="text-green-400">
                                        ✓ {dbResult.count || dbResult.data?.length || 0} apparati trovati
                                      </span>
                                    ) : (
                                      <span className="text-yellow-400">
                                        ⚠️ Nessun apparato trovato per questo cliente
                                      </span>
                                    )
                                  ) : (
                                    <span className="text-red-400">✗ Query non eseguita</span>
                                  )}
                                </div>
                              );
                            })()
                          ) : null}

                          {/* Processing Time */}
                          {msg.processing_time_ms != null && msg.processing_time_ms > 0 && (
                            <div className="text-xs text-slate-500">
                              ⏱️ Elaborato in {msg.processing_time_ms}ms
                            </div>
                          )}

                          {/* Error */}
                          {!!msg.error_occurred && msg.error_message ? (
                            <div className="text-xs text-red-400">
                              ❌ {msg.error_message}
                            </div>
                          ) : null}

                          {/* Database Result Details (Espandibile - Accordion) */}
                          {msg.database_query_result && (
                            (() => {
                              // Parse database_query_result se è una stringa JSON
                              let dbResult = msg.database_query_result;
                              if (typeof dbResult === 'string') {
                                try {
                                  dbResult = JSON.parse(dbResult);
                                } catch {
                                  return null;
                                }
                              }
                              // Verifica che ci siano dati
                              if (!dbResult || !dbResult.data || !Array.isArray(dbResult.data) || dbResult.data.length === 0) {
                                return null;
                              }
                              return (
                                <div className="text-xs mt-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedSection(expandedSection === `db-${msg.id}` ? null : `db-${msg.id}`);
                                    }}
                                    className="cursor-pointer text-primary-400 hover:text-primary-300 flex items-center gap-1"
                                  >
                                    <span className={`transition-transform ${expandedSection === `db-${msg.id}` ? 'rotate-90' : ''}`}>▶</span>
                                    📊 Dati database usati per la risposta ({dbResult.data.length} record)
                                  </button>
                                  {expandedSection === `db-${msg.id}` && (
                                    <div className="mt-2 p-2 bg-slate-800 rounded border border-slate-600 overflow-auto max-h-60">
                                      {dbResult.data.map((record: any, idx: number) => (
                                        <div key={idx} className="mb-3 pb-3 border-b border-slate-700 last:border-0 last:mb-0 last:pb-0">
                                          <div className="text-yellow-400 font-medium mb-1">📋 Record {idx + 1}:</div>
                                          {record.customer_name && (
                                            <div className="text-slate-300">👤 Cliente: <span className="text-white font-medium">{record.customer_name}</span></div>
                                          )}
                                          {record.device_name && (
                                            <div className="text-slate-300">🔧 Apparato: <span className="text-white font-medium">{record.device_name}</span> ({record.device_type})</div>
                                          )}
                                          {record.device_code && (
                                            <div className="text-slate-300">🏷️ Codice: <span className="text-white font-mono">{record.device_code}</span></div>
                                          )}
                                          {record.current_status && (
                                            <div className="text-slate-300">📍 Status: <span className="text-primary-400 font-medium">{record.current_status}</span></div>
                                          )}
                                          {record.estimate_amount && (
                                            <div className="text-slate-300">💰 Importo: <span className="text-green-400 font-medium">€{record.estimate_amount}</span> {record.is_paid ? '(Pagato ✓)' : '(Da pagare)'}</div>
                                          )}
                                          {record.payment_link && (
                                            <div className="text-slate-300">🔗 Link: <span className="text-blue-400 text-xs break-all">{record.payment_link}</span></div>
                                          )}
                                          {record.estimated_delivery_date && (
                                            <div className="text-slate-300">📅 Consegna: <span className="text-white">{new Date(record.estimated_delivery_date).toLocaleDateString('it-IT')}</span></div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                          )}
                        </div>
                      )}

                      {/* Response Details (solo per messaggi outgoing) */}
                      {msg.direction === 'outgoing' && (msg.text_version || msg.voice_version) && (
                        <div className="border-t border-green-600/30 pt-2 mt-2">
                          <div className="text-xs font-medium text-green-400 mb-1.5">
                            📤 Dettagli Risposta Inviata:
                          </div>

                          {/* Text Version (dati tecnici) */}
                          {msg.text_version && (
                            <div className="mb-2">
                              <div className="text-xs text-slate-400 mb-1">
                                📝 Versione Testo (dati tecnici):
                              </div>
                              <div className="text-xs text-slate-300 bg-slate-800/50 p-2 rounded whitespace-pre-wrap font-mono">
                                {msg.text_version}
                              </div>
                            </div>
                          )}

                          {/* Voice Version (discorso completo) */}
                          {msg.voice_version && (
                            <div>
                              <div className="text-xs text-slate-400 mb-1">
                                🔊 Versione Voce (discorso completo):
                              </div>
                              <div className="text-xs text-slate-300 bg-slate-800/50 p-2 rounded whitespace-pre-wrap">
                                {msg.voice_version}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Risposta Generata - Mostra interpretazione AI e risposta inviata */}
                      {msg.response_generated && msg.direction === 'incoming' && (
                        <div className="border-t border-primary-600/30 pt-2 mt-2">
                          <div className="text-xs font-medium text-primary-400 mb-1.5">
                            🤖 Interpretazione AI e Risposta Inviata:
                          </div>
                          <div className="text-xs space-y-1">
                            <div className="text-slate-400">
                              <span className="font-medium">Interpretato come:</span>{' '}
                              <span className="text-white">{msg.ai_intent || 'N/A'}</span>
                              {msg.ai_sentiment && (
                                <span className="ml-2">| Tono: <span className={`font-medium ${
                                  msg.ai_sentiment === 'positive' ? 'text-green-400' :
                                  msg.ai_sentiment === 'negative' ? 'text-yellow-400' :
                                  msg.ai_sentiment === 'angry' ? 'text-red-400' :
                                  'text-blue-400'
                                }`}>{msg.ai_sentiment}</span></span>
                              )}
                            </div>
                            {msg.database_query_result && (
                              <div className="text-slate-400">
                                <span className="font-medium">Dati consultati:</span>{' '}
                                <span className="text-white">
                                  {msg.database_query_result.data?.length || 0} record trovati
                                  {msg.database_query_result.data && msg.database_query_result.data[0] && (
                                    <span className="text-primary-300">
                                      {' '}({msg.database_query_result.data[0].device_name || msg.database_query_result.data[0].customer_name || 'apparato'} - status: {msg.database_query_result.data[0].current_status})
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Checkbox selezione per messaggi outgoing (dopo il messaggio) */}
                    {selectionMode && msg.direction === 'outgoing' && (
                      <label className="flex items-center cursor-pointer mt-3">
                        <input
                          type="checkbox"
                          checked={selectedMessageIds.has(msg.id)}
                          onChange={() => toggleMessageSelection(msg.id)}
                          className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-primary-600 focus:ring-primary-500 cursor-pointer"
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal Conferma Eliminazione Log */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-3">⚠️ Conferma Eliminazione</h3>
            <p className="text-slate-300 mb-4">
              Sei sicuro di voler eliminare TUTTI i log delle conversazioni?
            </p>
            <p className="text-sm text-slate-400 mb-6">
              Questa azione eliminerà:
              <br />• {conversations.length} conversazioni
              <br />• Tutti i messaggi associati
              <br /><br />
              <strong className="text-red-400">Questa operazione non può essere annullata!</strong>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowClearConfirm(false)}
                disabled={clearing}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-medium transition disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleClearLogs}
                disabled={clearing}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium transition disabled:opacity-50"
              >
                {clearing ? 'Eliminazione...' : 'Elimina Tutto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conferma Eliminazione Conversazione Singola */}
      {conversationToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-3">🗑️ Elimina Conversazione</h3>
            <p className="text-slate-300 mb-4">
              Eliminare la conversazione con <strong className="text-white">{conversationToDelete.name}</strong>?
            </p>
            <p className="text-sm text-slate-400 mb-6">
              Verranno eliminati:
              <br />• {conversationToDelete.count} messaggi
              <br /><br />
              <strong className="text-red-400">Questa operazione non può essere annullata!</strong>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConversationToDelete(null)}
                disabled={deletingConversation}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-medium transition disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteConversation}
                disabled={deletingConversation}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium transition disabled:opacity-50"
              >
                {deletingConversation ? 'Eliminazione...' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
